import { FC, useEffect, useState } from "react";
import { Col, Row, Tag } from "antd";
import moment from "moment";
import Files from "../../api/Files";
import { downloadRawFileFromServer } from "../../api/helpers";
import { IAntTableScroll } from "../ExpandedTable/ExpandedTable";
import { DraggableModal, FormField, IButton, UploadFile, ITooltip } from "..";
import { useApprovalState } from "../../hooks/useApprovalState";
import Constants from "../../constants/Common";
import { convertDate } from "../../utils/helpers/data.helpers";

import styles from "./styles.module.scss";

interface IProps {
  entityId?: number;
  entityName?: string;
  onHandleApproveOrReject: () => void;
  onClose: () => void;
  defaultPosition?: { x: number; y: number };
  destroyOnClose?: boolean;
  scroll?: IAntTableScroll;
  style?: {
    [index: string]: string;
  };
  width?: string | number;
}

const defaultScroll = {
  scrollToFirstRowOnChange: true,
  y: 240,
  x: "max-content",
};

export const ApprovalFormModal: FC<IProps> = ({
  entityId,
  entityName,
  onHandleApproveOrReject,
  onClose,
  defaultPosition,
  destroyOnClose = true,
  scroll = defaultScroll,
  style,
  width,
}) => {
  scroll = {
    ...defaultScroll,
    ...scroll,
  };

  const [tagParams, setTagParams] = useState(null);

  const DEFAULT_VALUES = { entityName, entityId, comment: "", fileId: "" };

  const approvalState = useApprovalState(DEFAULT_VALUES);

  useEffect(() => {
    setTagParams(approvalStatus());
  }, [approvalState?.approvalState]);

  const approvalStatus = () => {
    const state = approvalState?.approvalState;
    if (!state) {
      return;
    }
    const params = {
      color: "blue",
      children: state?.entityStateName,
    };

    if (state?.entityState < 1) return params;

    params.children = state?.status;

    params.color =
      state?.entityState === 1
        ? "yellow"
        : state?.entityState === 2
        ? "green"
        : state?.entityState === 3
        ? "red"
        : "blue";

    return params;
  };

  const onClickStartAgreement = async (e) => {
    e.preventDefault();
    await approvalState.onStartAgreement();
    onHandleApproveOrReject();
  };

  const onClickApproveOrReject = async () => {
    await approvalState.approveOrReject(approvalState.openApproveModal);
    onHandleApproveOrReject();
  };

  const downloadFile = async ({ id, originalName }) => {
    const res = await Files.getFileById(id);
    downloadRawFileFromServer(res.data, originalName);
  };

  return (
    <DraggableModal
      visible={!!entityId}
      onCancel={() => {
        approvalState.handleCloseApproveModal();
        onClose();
      }}
      title={
        <Row className={styles.approvalHistory__modalHeader} gutter={[10, 10]}>
          <Col>Текущий статус</Col>
          {tagParams && (
            <Col>
              <Tag {...approvalStatus()} />
            </Col>
          )}
        </Row>
      }
      footer={null}
      style={style}
      width={width}
      defaultPosition={defaultPosition}
      destroyOnClose={destroyOnClose}
    >
      <div className={styles.approvalHistory__body}>
        <table className={styles.approvalHistory__table}>
          <thead className={styles.approvalHistory__header}>
            <tr className={styles.approvalHistory__tableGroupHead}>
              <th>Статус</th>
              <th>Дата и время</th>
              <th>ФИО изменившего</th>
              <th>Комментарий</th>
              <th>Вложение</th>
            </tr>
          </thead>
          <tbody>
            {approvalState.approvalHistory?.length > 0 ? (
              approvalState.approvalHistory?.map((item, idx) => {
                return (
                  <th
                    className={styles.approvalHistory__tableGroupTbody}
                    key={idx}
                  >
                    <td>{item.status}</td>
                    <td>
                      {item.statusDate
                        ? moment(item.statusDate).format(
                            Constants.dateTimeFormat,
                          )
                        : Constants.notSet}
                    </td>
                    <td>{item.userFio}</td>
                    <td>{item.comment}</td>
                    <td className={styles.table__file}>
                      {item.file ? (
                        <ITooltip
                          className={styles.tooltip}
                          title={item.file.originalName}
                          placement="bottomLeft"
                        >
                          <a
                            className={styles.table__link}
                            onClick={(e) => downloadFile(item.file)}
                          >
                            {item.file.originalName}
                          </a>
                        </ITooltip>
                      ) : (
                        "-"
                      )}
                    </td>
                  </th>
                );
              })
            ) : (
              <tr>
                <th className={styles.approvalHistory__warning}>Нет данных</th>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {!approvalState.openApproveModal &&
        !approvalState.openRejectionModal &&
        !approvalState.isLoadingState && (
          <Row gutter={[10, 10]}>
            {approvalState.approvalState?.agreementNeeded &&
              approvalState.approvalState?.nextStepAvailable &&
              approvalState.approvalState?.entityState == 0 && (
                <Col>
                  <IButton onClick={onClickStartAgreement}>
                    Отправить на согласование
                  </IButton>
                </Col>
              )}
            {approvalState.approvalState?.entityState > 0 &&
              approvalState.approvalState?.agreementNeeded &&
              approvalState.approvalState?.nextStepAvailable && (
                <>
                  <Col>
                    <IButton
                      onClick={(e) => {
                        e.preventDefault();
                        approvalState.handleOpenApproveModal();
                      }}
                    >
                      Согласовать
                    </IButton>
                  </Col>
                  <Col>
                    <IButton
                      onClick={(e) => {
                        e.preventDefault();
                        approvalState.handleOpenRejectionModal();
                      }}
                    >
                      Отклонить
                    </IButton>
                  </Col>
                </>
              )}
            {!approvalState.approvalState?.routeCompleted &&
              !approvalState.approvalState?.nextStepAvailable &&
              "Нет доступа к согласованию"}
          </Row>
        )}
      {(approvalState.openApproveModal || approvalState.openRejectionModal) && (
        <Row justify="end" gutter={[10, 10]}>
          <Col span={24}>
            <FormField
              type="textarea"
              name={"comment"}
              register={approvalState.approvalForm.register}
              currentValue={approvalState.approvalForm.watchAllField["comment"]}
              label={
                approvalState.openApproveModal
                  ? "Введите примечание к согласованию"
                  : approvalState.openRejectionModal
                  ? "Укажите причину отклонения"
                  : ""
              }
              isRequired={
                (approvalState.openApproveModal &&
                  approvalState.approvalState?.nextStepParams
                    .commentConfirmRequired) ||
                (approvalState.openRejectionModal &&
                  approvalState.approvalState?.nextStepParams
                    .commentRejectRequired)
              }
            />
          </Col>
          <Col span={24}>
            <UploadFile
              accept="image"
              multiple={false}
              label="Загрузить файл"
              onChange={approvalState.onHandleFileChange}
            />
          </Col>
          <Col>
            <IButton
              disabled={!approvalState.approvalForm.formState.isValid}
              type={"button"}
              onClick={onClickApproveOrReject}
            >
              {approvalState.openApproveModal
                ? "Согласовать"
                : approvalState.openRejectionModal
                ? "Отклонить"
                : ""}
            </IButton>
          </Col>
        </Row>
      )}
    </DraggableModal>
  );
};
